import React, { useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { showAlert } from 'actions/app';
import { StatusManager } from 'common/components/StatusManager';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { ENDPOINTS } from 'features/command-center/constants';
import { SECONDARY_API_CLIENT } from 'services/API';
import strings from 'strings';
import FPIconButton from 'UI/components/atoms/FPIconButton';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { UIStatus } from 'UI/constants/status';
import { DRAWER_CONTENT_TYPES } from 'UI/pages/CommandCenter/OverallPerformance/OverallPerformance.constants';
import { SvgArrowLeft } from 'UI/res';
import { IMAGE_VALIDATION_TEXT } from 'UI/utils';

import { DrawerContent } from '../DrawerContent';
import { FORM_FIELDS_MAP } from '../FeedbackNoteForm/FeedbackNoteForm.constants';

import { useStyles } from './FeedbackNoteDrawer.styles';

const { feedbackNotes: STRINGS } = strings.commandCenter;

export const FeedbackNoteDrawer = ({ initialDrawerContent, onClose, selectedEmployee }) => {
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [initialFeedbackNote, setInitialFeedbackNote] = useState(null);
  const [uiState, setUIState] = useState({
    drawerContent: initialDrawerContent,
    isLoading: false,
    isSaving: false
  });
  const employeeName = selectedEmployee?.recruiterName?.name || selectedEmployee?.name;
  const employeeTeam = selectedEmployee?.team?.team || selectedEmployee?.managerName?.team;

  const isFeedbackLogView = uiState.drawerContent === DRAWER_CONTENT_TYPES.FeedbackLog;

  const classes = useStyles({ isFeedbackLogView });
  const dispatch = useDispatch();

  const form = useForm();
  const { handleSubmit, reset, errors } = form;

  const isUploadingEditorTextImage =
    errors[FORM_FIELDS_MAP.Description.key]?.message === IMAGE_VALIDATION_TEXT;

  const handleNewNoteClick = () => {
    setUIState(prev => ({ ...prev, drawerContent: DRAWER_CONTENT_TYPES.NotesForm }));
  };

  const cleanForm = () =>
    reset({
      [FORM_FIELDS_MAP.ReachOutReason.key]: '',
      [FORM_FIELDS_MAP.AddressableIssues.key]: '',
      [FORM_FIELDS_MAP.Description.key]: '',
      [FORM_FIELDS_MAP.Contract.key]: ''
    });

  const closeForm = () => {
    setSelectedNoteId(null);
    setInitialFeedbackNote(null);

    setUIState(prev => ({
      ...prev,
      drawerContent: DRAWER_CONTENT_TYPES.FeedbackLog
    }));
    cleanForm();
  };

  const onSubmit = async formData => {
    try {
      setUIState(prev => ({
        ...prev,
        isSaving: true
      }));
      const request = selectedNoteId ? SECONDARY_API_CLIENT.put : SECONDARY_API_CLIENT.post;
      const endpoint = selectedNoteId
        ? `${ENDPOINTS.feedbackNotesById.replace(':id', selectedNoteId)}`
        : ENDPOINTS.feedbackNotes;

      const addressableIssueIds = formData[FORM_FIELDS_MAP.AddressableIssues.key]
        .map(item => item.id)
        .join(',');

      const dataToSend = {
        reachOutReasonId: formData[FORM_FIELDS_MAP.ReachOutReason.key].id,
        addressableIssueIds,
        description: formData[FORM_FIELDS_MAP.Description.key],
        contract: formData[FORM_FIELDS_MAP.Contract.key]
      };

      if (!selectedNoteId) {
        dataToSend.empNo = selectedEmployee?.empNo;
      }

      const action = selectedNoteId ? 'updated' : 'created';

      const response = await request(endpoint, dataToSend);

      if (
        [HTTPStatusCodes.Ok, HTTPStatusCodes.Created, HTTPStatusCodes.NoContent].includes(
          response.status
        )
      ) {
        dispatch(
          showAlert({
            severity: 'success',
            title: STRINGS.title,
            body: strings.formatString(STRINGS.drawer.form.alert.success, {
              action
            })
          })
        );
        closeForm();
      }
    } catch (error) {
      dispatch(
        showAlert({
          severity: 'error',
          title: STRINGS.title,
          autoHideDuration: 5000,
          body: STRINGS.drawer.form.alert.error
        })
      );
    } finally {
      setUIState(prev => ({
        ...prev,
        isSaving: false
      }));
    }
  };

  const defaultHeaderProps = {
    avatar: (
      <FPIconButton
        onClick={closeForm}
        iconMode="custom"
        className={classes.iconButton}
        icon={SvgArrowLeft}
        tooltipProps={{ title: 'Back' }}
      />
    )
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.close,
        variant: 'outlined',
        onClick: onClose
      }
    },
    {
      props: {
        initialText: strings.shared.ui.save,
        onProgressText: strings.shared.ui.saving,
        disabled: isUploadingEditorTextImage || uiState.isLoading || uiState.isSaving,
        isSaving: uiState.isSaving
      },
      mode: 'save',
      visible: !isFeedbackLogView
    }
  ];

  return (
    <FPDrawer
      CardHeaderProps={{
        title: employeeName,
        subheader: employeeTeam,
        ...(!isFeedbackLogView ? defaultHeaderProps : {})
      }}
      FPDrawerProps={{
        open: !!selectedEmployee?.empNo,
        classes: { paper: classes.drawerLarge }
      }}
      ContentWrapper="form"
      ContentWrapperProps={{ onSubmit: handleSubmit(onSubmit) }}
      contentContainerClassName={classes.drawerContent}
      hasPaddingContent={false}
      onClose={onClose}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <StatusManager status={uiState.isLoading ? UIStatus.Loading : UIStatus.Success}>
        <FormContext {...form}>
          <DrawerContent
            drawerContent={uiState.drawerContent}
            employeeNumber={selectedEmployee?.empNo}
            initialFeedbackNote={initialFeedbackNote}
            onNewItemClick={handleNewNoteClick}
            selectedNoteId={selectedNoteId}
          />
        </FormContext>
      </StatusManager>
    </FPDrawer>
  );
};
